<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import WorkforceAPI from 'dashboard/api/workforce';
import WorkforceButton from '../components/WorkforceButton.vue';
import WorkforceModal from '../components/WorkforceModal.vue';
import WorkforceSelect from '../components/WorkforceSelect.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const IDLE_WARNING_THRESHOLD = 10 * 60; // seconds
const PENDING_WARNING_THRESHOLD = 5;
const SHIFT_END_WARNING_THRESHOLD = 30 * 60; // seconds

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getAgents');

const liveAgents = ref([]);
const loading = ref(false);
const searchTerm = ref('');
const statusFilter = ref('all');
const autoRefresh = ref(true);
const refreshTimer = ref(null);
const tickTimer = ref(null);
const metricsNow = ref(Date.now());
const metricsFetchedAt = ref(Date.now());
const wsRefreshDebounceTimer = ref(null);
const lastUpdatedAt = ref(null);
const selectedAgentIds = ref([]);
const expandedAgents = ref([]);
const metricsHistory = ref([]);
const liveMeta = ref({
  averages: {},
  hr_overview: {},
  alerts: [],
  snapshot: null,
});

const reassignState = reactive({
  open: false,
  loading: false,
  agent: null,
  conversations: [],
});

const batchReassignState = reactive({
  open: false,
  loading: false,
  conversations: [],
  agents: [],
});
const reminderState = reactive({
  open: false,
  submitting: false,
  conversation: null,
  agent: null,
  notes: '',
  conversationId: null,
});

const matchesStatusFilter = agent => {
  // Always filter out agents who have ended their shift
  if (agent.session && agent.session.status !== 'open') {
    return false;
  }

  // Only show agents who are on shift (have an active session)
  if (!agent.session || agent.session.status !== 'open') {
    return false;
  }

  if (statusFilter.value === 'all') return true;
  if (statusFilter.value === 'online') {
    return agent.availability === 'online';
  }
  if (statusFilter.value === 'busy') {
    return agent.availability === 'busy';
  }
  if (statusFilter.value === 'break') {
    return !!agent.session?.active_break;
  }
  if (statusFilter.value === 'offline') {
    return agent.availability === 'offline' && !agent.session?.active_break;
  }
  return true;
};

const hrOverview = computed(() => liveMeta.value?.hr_overview || {});
const averageWaitSeconds = computed(
  () => liveMeta.value?.averages?.average_wait_seconds || 0
);

const secondsToDurationLabel = seconds => {
  if (!seconds || Number.isNaN(seconds)) return '--';
  if (seconds < 60) {
    return `${Math.max(Math.round(seconds), 1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const residualSeconds = Math.round(seconds % 60);
  if (minutes < 60) {
    return `${minutes}m ${residualSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const residualMinutes = minutes % 60;
  return `${hours}h ${residualMinutes}m`;
};

const averageWaitLabel = computed(() =>
  secondsToDurationLabel(averageWaitSeconds.value)
);

const joinList = items => {
  if (!items || (Array.isArray(items) && items.length === 0)) return '—';
  if (Array.isArray(items)) return items.join(', ');
  return String(items);
};

const alertAgentName = alert =>
  alert.agent_name || alert.agent?.name || t('WORKFORCE.LABELS.UNKNOWN_AGENT');

const alertIconClass = alert => {
  switch (alert.type) {
    case 'idle':
      return 'i-lucide-coffee';
    case 'overload':
      return 'i-lucide-triangle-alert';
    case 'shift_end':
      return 'i-lucide-hourglass';
    case 'occupancy':
      return 'i-lucide-gauge';
    case 'absence':
      return 'i-lucide-clock-1';
    case 'sla':
      return 'i-lucide-alert-octagon';
    default:
      return 'i-lucide-info';
  }
};

const alertToneClass = alert => {
  switch (alert.type) {
    case 'idle':
      return 'bg-amber-3/40 text-amber-11';
    case 'overload':
    case 'sla':
      return 'bg-ruby-3/40 text-ruby-11';
    case 'shift_end':
      return 'bg-teal-3/40 text-teal-11';
    case 'occupancy':
      return 'bg-indigo-3/40 text-indigo-11';
    case 'absence':
      return 'bg-amber-3/40 text-amber-11';
    default:
      return 'bg-n-weak/30 text-n-slate-10';
  }
};

const alertMessage = alert => {
  switch (alert.type) {
    case 'idle':
      return t('WORKFORCE.LIVE.ALERTS.IDLE', { minutes: alert.minutes });
    case 'overload':
      return t('WORKFORCE.LIVE.ALERTS.OVERLOAD', { pending: alert.pending });
    case 'shift_end':
      return t('WORKFORCE.LIVE.ALERTS.SHIFT_END', { minutes: alert.minutes });
    case 'occupancy':
      return t('WORKFORCE.LIVE.ALERTS.OCCUPANCY', { value: alert.occupancy });
    case 'absence':
      return t('WORKFORCE.LIVE.ALERTS.ABSENCE');
    case 'sla':
      return t('WORKFORCE.LIVE.ALERTS.SLA', {
        ids: (alert.conversations || []).join(', '),
      });
    default:
      return '';
  }
};

const waitingDurationLabel = seconds => secondsToDurationLabel(seconds || 0);

const hrOverviewCards = computed(() => {
  const hr = hrOverview.value;
  return [
    {
      key: 'on_shift',
      label: t('WORKFORCE.LIVE.HR.ON_SHIFT'),
      value: hr.on_shift ?? 0,
    },
    {
      key: 'on_break',
      label: t('WORKFORCE.LIVE.HR.ON_BREAK'),
      value: hr.on_break ?? 0,
    },
    {
      key: 'scheduled_now',
      label: t('WORKFORCE.LIVE.HR.SCHEDULED_NOW'),
      value: hr.scheduled_now ?? 0,
    },
    {
      key: 'scheduled_absent',
      label: t('WORKFORCE.LIVE.HR.SCHEDULED_ABSENT'),
      value: hr.scheduled_absent ?? 0,
    },
    {
      key: 'active_idle',
      label: t('WORKFORCE.LIVE.HR.ACTIVE_IDLE'),
      value: hr.active_idle ?? 0,
    },
    {
      key: 'offline',
      label: t('WORKFORCE.LIVE.HR.OFFLINE_TOTAL'),
      value: (hr.offline_active || 0) + (hr.offline_passive || 0),
      hint: t('WORKFORCE.LIVE.HR.OFFLINE_HINT', {
        active: hr.offline_active || 0,
        passive: hr.offline_passive || 0,
      }),
    },
  ];
});

const formatTimestamp = value => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString();
};

const anomalyLabel = anomaly => {
  if (!anomaly || !anomaly.type) return '';
  switch (anomaly.type) {
    case 'short_shift':
      return t('WORKFORCE.LIVE.ANOMALIES.SHORT_SHIFT', anomaly.details || {});
    case 'insufficient_break':
      return t(
        'WORKFORCE.LIVE.ANOMALIES.INSUFFICIENT_BREAK',
        anomaly.details || {}
      );
    case 'idle_period':
      return t('WORKFORCE.LIVE.ANOMALIES.IDLE_PERIOD', {
        minutes: Math.round((anomaly.duration_seconds || 0) / 60),
      });
    default:
      return anomaly.type;
  }
};

const shiftElapsedLabel = agent => {
  const startedAt = agent.session?.started_at;
  if (!startedAt) return t('WORKFORCE.STATUS.NO_ACTIVE_SHIFT');
  const diffSeconds = Math.max(
    (Date.now() - new Date(startedAt).getTime()) / 1000,
    0
  );
  return secondsToDurationLabel(diffSeconds);
};

const breakTimerLabel = agent => {
  const breakStarted = agent.session?.active_break;
  if (!breakStarted) return t('WORKFORCE.STATUS.NO_ACTIVE_BREAK');
  const diffSeconds = Math.max(
    (Date.now() - new Date(breakStarted).getTime()) / 1000,
    0
  );
  return secondsToDurationLabel(diffSeconds);
};

const occupancyLabel = agent => {
  const ratio = agent.session?.occupancy_ratio;
  if (ratio === null || ratio === undefined) return '—';
  return `${Math.round(ratio * 100)}%`;
};

const timeSinceLabel = seconds => {
  if (seconds === null || seconds === undefined) return '—';
  const base = Number(seconds);
  if (Number.isNaN(base)) return '—';
  const inflated = Math.max(
    0,
    base + Math.floor((metricsNow.value - metricsFetchedAt.value) / 1000)
  );
  if (inflated < 1) return t('WORKFORCE.LIVE.TABLE.JUST_NOW');
  return secondsToDurationLabel(inflated);
};

const nowSeconds = computed(() => Math.floor(metricsNow.value / 1000));
const sameLocalDay = (epochSecondsA, epochSecondsB) => {
  if (!epochSecondsA || !epochSecondsB) return false;
  return (
    new Date(epochSecondsA * 1000).toDateString() ===
    new Date(epochSecondsB * 1000).toDateString()
  );
};
const lastReplyAtEpoch = agent => {
  const seconds = agent?.time_since_last_reply_seconds;
  if (seconds === null || seconds === undefined) return null;
  const base = Number(seconds);
  if (Number.isNaN(base)) return null;
  const inflated = Math.max(
    0,
    base + Math.floor((metricsNow.value - metricsFetchedAt.value) / 1000)
  );
  return Math.max(0, nowSeconds.value - inflated);
};
const replyActivityToday = agent => {
  const last = lastReplyAtEpoch(agent);
  const isToday = last && sameLocalDay(last, nowSeconds.value);
  if (!last || !isToday) {
    return {
      tone: 'none',
      label: 'No reply today',
      title: 'No replies from this agent today',
    };
  }
  const d = new Date(last * 1000);
  return {
    tone: 'ok',
    label: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    title: `Last reply today at ${d.toLocaleString()}`,
  };
};

const filteredAgents = computed(() => {
  let base = liveAgents.value.filter(matchesStatusFilter);
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    base = base.filter(
      agent =>
        agent.name.toLowerCase().includes(term) ||
        agent.email?.toLowerCase().includes(term)
    );
  }
  return base;
});

watch(filteredAgents, agentsList => {
  const visibleIds = agentsList.map(agent => agent.user_id);
  selectedAgentIds.value = selectedAgentIds.value.filter(id =>
    visibleIds.includes(id)
  );
});

watch(
  () => reminderState.conversationId,
  id => {
    if (!reminderState.agent || !id) return;
    const nextConversation = reminderState.agent.conversations?.find(
      convo => String(convo.id) === String(id)
    );
    if (nextConversation) {
      reminderState.conversation = nextConversation;
    }
  }
);

const fetchLiveStatus = async () => {
  loading.value = true;
  try {
    const response = await WorkforceAPI.getLiveStatus();
    const agentsPayload = response?.agents || [];
    liveAgents.value = agentsPayload;
    liveMeta.value = response?.meta || {
      averages: {},
      hr_overview: {},
      alerts: [],
      snapshot: null,
    };
    lastUpdatedAt.value = new Date();
    metricsFetchedAt.value = Date.now();
    metricsNow.value = Date.now();
    if (liveMeta.value.snapshot) {
      recordMetricsSnapshot(liveMeta.value.snapshot);
    } else {
      recordMetricsSnapshot({
        timestamp: new Date(),
        active_agents: agentsPayload.filter(agent =>
          ['online', 'busy'].includes(agent.availability)
        ).length,
        on_break: agentsPayload.filter(agent => agent.session?.active_break)
          .length,
        pending_conversations: agentsPayload.reduce(
          (sum, agent) => sum + (agent.pending_conversations_count || 0),
          0
        ),
      });
    }
  } catch (error) {
    useAlert(t('WORKFORCE.LIVE.TOASTS.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const scheduleWsRefresh = () => {
  if (wsRefreshDebounceTimer.value) {
    clearTimeout(wsRefreshDebounceTimer.value);
    wsRefreshDebounceTimer.value = null;
  }
  wsRefreshDebounceTimer.value = setTimeout(() => {
    fetchLiveStatus();
    wsRefreshDebounceTimer.value = null;
  }, 1200);
};

const handleWsEvent = payload => {
  const { event, data } = payload || {};
  if (event !== 'message.created' && event !== 'message.updated') return;

  const messageType = Number(data?.message_type);
  const senderType = String(data?.sender_type || '');

  // message_type: 1 = outgoing; sender_type: 'User' = agent
  if (messageType === 1 && senderType === 'User') {
    // Refresh soon to update last reply/activity counters without manual refresh
    scheduleWsRefresh();
  }
};

const startAutoRefresh = () => {
  stopAutoRefresh();
  if (!autoRefresh.value) return;
  refreshTimer.value = setInterval(fetchLiveStatus, 30000);
};

const stopAutoRefresh = () => {
  if (refreshTimer.value) {
    clearInterval(refreshTimer.value);
    refreshTimer.value = null;
  }
};

const startTicking = () => {
  if (tickTimer.value) clearInterval(tickTimer.value);
  tickTimer.value = setInterval(() => {
    metricsNow.value = Date.now();
  }, 1000);
};
const stopTicking = () => {
  if (tickTimer.value) {
    clearInterval(tickTimer.value);
    tickTimer.value = null;
  }
};

const statusBadgeClass = agent => {
  if (agent.session?.active_break) return 'bg-amber-3/40 text-amber-11';
  if (agent.availability === 'online') return 'bg-teal-3/40 text-teal-11';
  if (agent.availability === 'busy') return 'bg-amber-3/40 text-amber-11';
  return 'bg-n-weak/30 text-n-slate-10';
};

const statusLabel = agent => {
  if (agent.session?.active_break) return t('WORKFORCE.STATUS.ON_BREAK');
  if (agent.availability === 'online') return t('WORKFORCE.STATUS.ONLINE');
  if (agent.availability === 'busy') return t('WORKFORCE.STATUS.BUSY');
  return t('WORKFORCE.STATUS.OFFLINE');
};

const remainingShiftLabel = agent => {
  const seconds = agent.session?.remaining_shift_seconds;
  if (!seconds || seconds <= 0) return t('WORKFORCE.STATUS.NO_SCHEDULE');
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return t('WORKFORCE.STATUS.ENDS_IN_MIN', { count: minutes });
  }
  const hours = Math.floor(minutes / 60);
  const residual = minutes % 60;
  return t('WORKFORCE.STATUS.ENDS_IN_HR_MIN', { hours, minutes: residual });
};

const recordMetricsSnapshot = snapshot => {
  if (!snapshot) return;

  const parsed = {
    timestamp: snapshot.timestamp ? new Date(snapshot.timestamp) : new Date(),
    activeAgents: snapshot.active_agents ?? snapshot.activeAgents ?? 0,
    pendingConversations:
      snapshot.pending_conversations ?? snapshot.pendingConversations ?? 0,
    onBreak: snapshot.on_break ?? snapshot.onBreak ?? 0,
  };

  metricsHistory.value = [...metricsHistory.value.slice(-11), parsed];
};

const summarizedMetrics = computed(() => {
  const hr = hrOverview.value;
  const totalAgents = hr.total_agents ?? liveAgents.value.length;
  const onDuty =
    hr.on_shift ??
    liveAgents.value.filter(
      agent => agent.availability !== 'offline' || agent.session?.active_break
    ).length;
  const onBreak =
    hr.on_break ??
    liveAgents.value.filter(agent => agent.session?.active_break).length;
  const idleRisk =
    hr.active_idle ??
    liveAgents.value.filter(
      agent =>
        agent.idle_event?.duration_seconds &&
        agent.idle_event.duration_seconds >= IDLE_WARNING_THRESHOLD
    ).length;
  const pendingTotal =
    liveMeta.value?.snapshot?.pending_conversations ??
    liveAgents.value.reduce(
      (sum, agent) => sum + (agent.pending_conversations_count || 0),
      0
    );
  const closingSoon = liveAgents.value.filter(agent => {
    const remaining = agent.session?.remaining_shift_seconds;
    return (
      remaining && remaining > 0 && remaining <= SHIFT_END_WARNING_THRESHOLD
    );
  }).length;

  return {
    totalAgents,
    onDuty,
    onBreak,
    idleRisk,
    pendingTotal,
    avgPending: totalAgents ? (pendingTotal / totalAgents).toFixed(1) : '0.0',
    closingSoon,
  };
});

const kpiCards = computed(() => {
  const hr = hrOverview.value;
  return [
    {
      key: 'onShift',
      label: t('WORKFORCE.LIVE.HR.ON_SHIFT'),
      value: hr.on_shift ?? summarizedMetrics.value.onDuty,
      subLabel: t('WORKFORCE.LIVE.KPI.TOTAL_AGENTS', {
        count: hr.total_agents ?? summarizedMetrics.value.totalAgents,
      }),
    },
    {
      key: 'scheduledNow',
      label: t('WORKFORCE.LIVE.HR.SCHEDULED_NOW'),
      value: hr.scheduled_now ?? 0,
      subLabel: hr.scheduled_absent
        ? t('WORKFORCE.LIVE.HR.SCHEDULED_BUT_ABSENT_COUNT', {
            count: hr.scheduled_absent,
          })
        : t('WORKFORCE.LIVE.KPI.ALL_PRESENT'),
    },
    {
      key: 'onBreak',
      label: t('WORKFORCE.LIVE.KPI.ON_BREAK'),
      value: hr.on_break ?? summarizedMetrics.value.onBreak,
      subLabel: hr.active_idle
        ? t('WORKFORCE.LIVE.HR.ACTIVE_IDLE_COUNT', { count: hr.active_idle })
        : t('WORKFORCE.LIVE.KPI.BREAK_HINT'),
    },
    {
      key: 'avgWait',
      label: t('WORKFORCE.LIVE.KPI.AVG_WAIT'),
      value: averageWaitLabel.value,
      subLabel: t('WORKFORCE.LIVE.KPI.AVG_WAIT_HINT'),
    },
    {
      key: 'pending',
      label: t('WORKFORCE.LIVE.KPI.PENDING'),
      value: summarizedMetrics.value.pendingTotal,
      subLabel: t('WORKFORCE.LIVE.KPI.AVG_PENDING', {
        value: summarizedMetrics.value.avgPending,
      }),
    },
    {
      key: 'closingSoon',
      label: t('WORKFORCE.LIVE.KPI.CLOSING_SOON'),
      value: summarizedMetrics.value.closingSoon,
      subLabel: t('WORKFORCE.LIVE.KPI.CLOSING_HINT'),
    },
  ];
});

const alertFeed = computed(() => {
  if (liveMeta.value?.alerts?.length) {
    return liveMeta.value.alerts;
  }

  const alerts = [];
  liveAgents.value.forEach(agent => {
    if (
      agent.idle_event?.duration_seconds &&
      agent.idle_event.duration_seconds >= IDLE_WARNING_THRESHOLD
    ) {
      alerts.push({
        type: 'idle',
        agent_id: agent.user_id,
        agent_name: agent.name,
        minutes: Math.round(agent.idle_event.duration_seconds / 60),
      });
    }
    if (
      agent.pending_conversations_count &&
      agent.pending_conversations_count >= PENDING_WARNING_THRESHOLD
    ) {
      alerts.push({
        type: 'overload',
        agent_id: agent.user_id,
        agent_name: agent.name,
        pending: agent.pending_conversations_count,
      });
    }
    const remaining = agent.session?.remaining_shift_seconds;
    if (
      remaining &&
      remaining > 0 &&
      remaining <= SHIFT_END_WARNING_THRESHOLD
    ) {
      alerts.push({
        type: 'shift_end',
        agent_id: agent.user_id,
        agent_name: agent.name,
        minutes: Math.ceil(remaining / 60),
      });
    }
  });
  return alerts;
});

const historySeries = computed(() =>
  metricsHistory.value.map(item => ({
    timestamp: item.timestamp,
    activeAgents: item.activeAgents,
    pendingConversations: item.pendingConversations,
    onBreak: item.onBreak,
  }))
);

const historyMax = computed(() => {
  if (!metricsHistory.value.length) {
    return { active: 1, pending: 1, break: 1 };
  }

  return metricsHistory.value.reduce(
    (acc, item) => ({
      active: Math.max(acc.active, item.activeAgents),
      pending: Math.max(acc.pending, item.pendingConversations),
      break: Math.max(acc.break, item.onBreak),
    }),
    { active: 1, pending: 1, break: 1 }
  );
});

const toggleSelectAgent = id => {
  if (selectedAgentIds.value.includes(id)) {
    selectedAgentIds.value = selectedAgentIds.value.filter(item => item !== id);
  } else {
    selectedAgentIds.value = [...selectedAgentIds.value, id];
  }
};

const toggleSelectAll = () => {
  const visibleIds = filteredAgents.value.map(agent => agent.user_id);
  const allSelected = visibleIds.every(id =>
    selectedAgentIds.value.includes(id)
  );
  selectedAgentIds.value = allSelected ? [] : visibleIds;
};

const isAgentSelected = id => selectedAgentIds.value.includes(id);

const toggleAgentExpansion = id => {
  if (expandedAgents.value.includes(id)) {
    expandedAgents.value = expandedAgents.value.filter(item => item !== id);
  } else {
    expandedAgents.value = [...expandedAgents.value, id];
  }
};

const openBatchReassignment = async () => {
  if (!selectedAgentIds.value.length) {
    useAlert(t('WORKFORCE.LIVE.TOASTS.NO_SELECTION'));
    return;
  }

  batchReassignState.open = true;
  batchReassignState.loading = true;
  batchReassignState.conversations = [];
  batchReassignState.agents = selectedAgentIds.value
    .map(id => liveAgents.value.find(agent => agent.user_id === id))
    .filter(Boolean);

  try {
    const responses = await Promise.all(
      batchReassignState.agents.map(agent =>
        WorkforceAPI.getReassignmentCandidates({ user_id: agent.user_id })
      )
    );

    const aggregated = responses.flatMap((response, index) => {
      const agent = batchReassignState.agents[index];
      const conversations = response?.data || [];
      return conversations.map(conversation => ({
        ...conversation,
        owner_user_id: agent.user_id,
        owner_name: agent.name,
        owner_email: agent.email,
        target_agent_id: null,
      }));
    });

    batchReassignState.conversations = aggregated;
    if (!aggregated.length) {
      useAlert(t('WORKFORCE.LIVE.TOASTS.REASSIGN_EMPTY'));
    }
  } catch (error) {
    useAlert(t('WORKFORCE.LIVE.TOASTS.REASSIGN_FETCH_ERROR'));
    batchReassignState.open = false;
  } finally {
    batchReassignState.loading = false;
  }
};

const closeBatchReassignment = () => {
  batchReassignState.open = false;
  batchReassignState.loading = false;
  batchReassignState.conversations = [];
  batchReassignState.agents = [];
};

const openReminderModal = (agent, conversation) => {
  reminderState.open = true;
  reminderState.agent = agent;
  reminderState.conversation = conversation;
  reminderState.notes = '';
  reminderState.conversationId = conversation?.id || null;
};

const closeReminderModal = () => {
  reminderState.open = false;
  reminderState.submitting = false;
  reminderState.notes = '';
  reminderState.conversation = null;
  reminderState.agent = null;
  reminderState.conversationId = null;
};

const submitReminder = async () => {
  if (!reminderState.conversation || !reminderState.agent) return;

  reminderState.submitting = true;
  try {
    await reassignConversation(
      reminderState.conversationId,
      reminderState.agent.user_id,
      {
        mode: 'reminder',
        notes: reminderState.notes,
      }
    );
    closeReminderModal();
  } catch (error) {
    reminderState.submitting = false;
  }
};

const openReassignment = async agent => {
  // Check if agent has any conversations to reassign
  if (
    !agent.pending_conversations_count ||
    agent.pending_conversations_count === 0
  ) {
    useAlert(t('WORKFORCE.LIVE.TOASTS.NO_CONVERSATIONS_TO_REASSIGN'));
    return;
  }

  reassignState.agent = agent;
  reassignState.loading = true;
  reassignState.conversations = [];
  reassignState.open = true;
  try {
    const response = await WorkforceAPI.getReassignmentCandidates({
      user_id: agent.user_id,
    });
    reassignState.conversations = (response.data || []).map(conversation => ({
      ...conversation,
      target_agent_id: null,
    }));

    // If no conversations found, show message
    if (reassignState.conversations.length === 0) {
      useAlert(t('WORKFORCE.LIVE.TOASTS.NO_CONVERSATIONS_TO_REASSIGN'));
      reassignState.open = false;
    }
  } catch (error) {
    console.error('[Reassignment] Error fetching candidates:', error);
    useAlert(t('WORKFORCE.LIVE.TOASTS.REASSIGN_FETCH_ERROR'));
    reassignState.open = false;
  } finally {
    reassignState.loading = false;
  }
};

const closeReassignment = () => {
  reassignState.open = false;
  reassignState.conversations = [];
  reassignState.agent = null;
};

const reassignConversation = async (
  conversationId,
  targetUserId,
  options = {}
) => {
  const normalizedTargetId = targetUserId ? Number(targetUserId) : null;
  const mode = options.mode || (normalizedTargetId ? 'manual' : 'unassign');
  try {
    const payload = {
      conversation_id: conversationId,
      target_user_id: normalizedTargetId,
      mode,
    };
    if (options.notes) {
      payload.notes = options.notes;
    }
    await WorkforceAPI.createReassignment(payload);
    const toastKey =
      mode === 'reminder'
        ? 'WORKFORCE.LIVE.TOASTS.REMINDER_SUCCESS'
        : 'WORKFORCE.LIVE.TOASTS.REASSIGN_SUCCESS';
    useAlert(t(toastKey));
    if (mode === 'batch') {
      batchReassignState.conversations =
        batchReassignState.conversations.filter(
          convo => convo.id !== conversationId
        );
      if (!batchReassignState.conversations.length) {
        closeBatchReassignment();
      }
    } else {
      reassignState.conversations = reassignState.conversations.filter(
        convo => convo.id !== conversationId
      );
      if (mode !== 'reminder' && !reassignState.conversations.length) {
        closeReassignment();
      }
    }
    fetchLiveStatus();
  } catch (error) {
    const errorKey =
      mode === 'reminder'
        ? 'WORKFORCE.LIVE.TOASTS.REMINDER_ERROR'
        : 'WORKFORCE.LIVE.TOASTS.REASSIGN_ERROR';
    useAlert(t(errorKey));
  }
};

const agentOptions = computed(() => agents.value);

const targetOptionsFor = agentId =>
  agentOptions.value.filter(option => Number(option.id) !== Number(agentId));

const allVisibleSelected = computed(() => {
  const visibleIds = filteredAgents.value.map(agent => agent.user_id);
  return (
    visibleIds.length > 0 &&
    visibleIds.every(id => selectedAgentIds.value.includes(id))
  );
});

onMounted(async () => {
  // Fetch live status immediately without waiting for agents
  await fetchLiveStatus();
  startAutoRefresh();
  startTicking();
  emitter.on(BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED, handleWsEvent);

  // Load agents in background if needed (non-blocking)
  if (!agents.value.length) {
    store.dispatch('agents/get').catch(() => {
      // Ignore errors, agents are optional for display
    });
  }
});

onBeforeUnmount(() => {
  stopAutoRefresh();
  stopTicking();
  emitter.off(BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED, handleWsEvent);
  if (wsRefreshDebounceTimer.value) clearTimeout(wsRefreshDebounceTimer.value);
  wsRefreshDebounceTimer.value = null;
});

watch(autoRefresh, value => {
  if (value) {
    startAutoRefresh();
  } else {
    stopAutoRefresh();
  }
});
</script>

<template>
  <section class="flex flex-col gap-5 px-8 py-5 lg:px-10">
    <!-- KPI Cards with color accent strips -->
    <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <div
        v-for="card in kpiCards"
        :key="card.key"
        class="relative overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-sm"
      >
        <div
          class="absolute left-0 top-0 h-full w-1 rounded-l-xl"
          :class="{
            'bg-indigo-9': card.key === 'onShift',
            'bg-teal-9': card.key === 'scheduledNow',
            'bg-amber-9': card.key === 'onBreak',
            'bg-sky-9': card.key === 'avgWait',
            'bg-ruby-9': card.key === 'pending',
            'bg-orange-9': card.key === 'closingSoon',
          }"
        />
        <p
          class="text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
        >
          {{ card.label }}
        </p>
        <p class="mt-2 text-2xl font-bold text-n-slate-12">
          {{ card.value }}
        </p>
        <p class="mt-1 text-[11px] text-n-slate-9">
          {{ card.subLabel }}
        </p>
      </div>
    </div>

    <!-- Control bar -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 px-5 py-3 shadow-sm"
    >
      <div class="flex items-center gap-3 text-xs text-n-slate-10">
        <span class="font-semibold text-n-slate-12">
          {{
            t('WORKFORCE.LIVE.METRIC.AGENTS_MONITORED', {
              count: liveAgents.length,
            })
          }}
        </span>
        <span class="hidden sm:inline">·</span>
        <span class="hidden sm:inline">
          {{
            t('WORKFORCE.LIVE.METRIC.LAST_UPDATED', {
              time: lastUpdatedAt ? lastUpdatedAt.toLocaleTimeString() : '—',
            })
          }}
        </span>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <label
          class="flex cursor-pointer items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-3 px-3 py-1.5 text-xs text-n-slate-10 transition hover:bg-n-solid-1"
        >
          <input
            v-model="autoRefresh"
            type="checkbox"
            class="size-3.5 rounded border-n-weak text-black focus:ring-n-strong/30 dark:text-white dark:focus:ring-white/30"
          />
          {{ t('WORKFORCE.LIVE.CONTROLS.AUTO_REFRESH') }}
        </label>
        <div class="w-36">
          <WorkforceSelect v-model="statusFilter">
            <option value="all">
              {{ t('WORKFORCE.LIVE.FILTER.STATUS_ALL') }}
            </option>
            <option value="online">
              {{ t('WORKFORCE.LIVE.FILTER.STATUS_ONLINE') }}
            </option>
            <option value="busy">
              {{ t('WORKFORCE.LIVE.FILTER.STATUS_BUSY') }}
            </option>
            <option value="break">
              {{ t('WORKFORCE.LIVE.FILTER.STATUS_BREAK') }}
            </option>
            <option value="offline">
              {{ t('WORKFORCE.LIVE.FILTER.STATUS_OFFLINE') }}
            </option>
          </WorkforceSelect>
        </div>
        <div class="relative">
          <input
            v-model="searchTerm"
            :placeholder="t('WORKFORCE.LIVE.SEARCH_PLACEHOLDER')"
            class="w-48 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 pr-8 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
          <span
            class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-n-slate-9"
          >
            <span class="i-lucide-search size-3.5" />
          </span>
        </div>
        <WorkforceButton
          size="sm"
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="loading"
          @click="fetchLiveStatus"
        >
          {{ t('WORKFORCE.ACTIONS.REFRESH') }}
        </WorkforceButton>
        <WorkforceButton
          size="sm"
          icon="i-lucide-layers"
          :disabled="!selectedAgentIds.length || batchReassignState.loading"
          @click="openBatchReassignment"
        >
          {{ t('WORKFORCE.LIVE.ACTIONS.BATCH_REASSIGN') }}
        </WorkforceButton>
      </div>
    </div>

    <div class="grid gap-5 xl:grid-cols-[2fr_1fr]">
      <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
        <div
          class="flex items-center justify-between border-b border-n-weak/60 px-5 py-3"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('WORKFORCE.LIVE.TABLE.TITLE') }}
          </h3>
          <span class="text-xs text-n-slate-10">
            {{
              t('WORKFORCE.LIVE.TABLE.COUNT', { count: filteredAgents.length })
            }}
          </span>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-n-weak text-sm">
            <thead
              class="bg-n-solid-2 text-left text-xs uppercase tracking-wide text-n-slate-10"
            >
              <tr>
                <th class="w-12 px-3 py-3">
                  <input
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-black focus:ring-n-strong/30 dark:text-white dark:focus:ring-white/30"
                    :checked="allVisibleSelected"
                    @change="toggleSelectAll"
                  />
                </th>
                <th class="w-12 px-3 py-3" />
                <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.AGENT') }}</th>
                <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.STATUS') }}</th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.LIVE.METRIC.PENDING_CONVERSATIONS') }}
                </th>
                <th class="px-5 py-3">{{ t('WORKFORCE.LIVE.TABLE.IDLE') }}</th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.LIVE.METRIC.REMAINING_SHIFT') }}
                </th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.LIVE.METRIC.SCHEDULED_NOW') }}
                </th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.LIVE.TABLE.TIME_SINCE_REPLY') }}
                </th>
                <th class="px-5 py-3">Agent activity</th>
                <th class="px-5 py-3">
                  {{ t('WORKFORCE.LIVE.TABLE.TIME_SINCE_ACTIVITY') }}
                </th>
                <th class="px-5 py-3 text-right">
                  {{ t('WORKFORCE.TABLE.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody
              class="divide-y divide-n-weak/70 bg-n-solid-3 text-n-slate-12"
            >
              <tr v-if="loading">
                <td
                  colspan="11"
                  class="px-5 py-6 text-center text-sm text-n-slate-10"
                >
                  {{ t('WORKFORCE.LIVE.TABLE.LOADING') }}
                </td>
              </tr>
              <tr v-else-if="!filteredAgents.length">
                <td
                  colspan="11"
                  class="px-5 py-6 text-center text-sm text-n-slate-10"
                >
                  {{ t('WORKFORCE.LIVE.EMPTY') }}
                </td>
              </tr>
              <template v-else>
                <template v-for="agent in filteredAgents" :key="agent.user_id">
                  <tr>
                    <td class="px-3 py-3 align-middle">
                      <input
                        type="checkbox"
                        class="size-4 rounded border-n-weak text-black focus:ring-n-strong/30 dark:text-white dark:focus:ring-white/30"
                        :checked="isAgentSelected(agent.user_id)"
                        @change="toggleSelectAgent(agent.user_id)"
                      />
                    </td>
                    <td class="px-3 py-3 align-middle">
                      <WorkforceButton
                        size="xs"
                        variant="ghost"
                        icon="i-lucide-chevron-down"
                        class="transition-transform duration-200"
                        :class="[
                          expandedAgents.includes(agent.user_id)
                            ? 'rotate-180'
                            : '',
                        ]"
                        @click="toggleAgentExpansion(agent.user_id)"
                      />
                    </td>
                    <td class="px-5 py-3">
                      <div class="flex flex-col">
                        <span class="font-medium">{{ agent.name }}</span>
                        <span class="text-xs text-n-slate-10">{{
                          agent.email
                        }}</span>
                      </div>
                    </td>
                    <td class="px-5 py-3">
                      <span
                        class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium"
                        :class="statusBadgeClass(agent)"
                      >
                        <span class="i-lucide-activity size-3" />
                        {{ statusLabel(agent) }}
                      </span>
                    </td>
                    <td class="px-5 py-3 font-semibold">
                      {{ agent.pending_conversations_count || 0 }}
                    </td>
                    <td class="px-5 py-3">
                      <span
                        v-if="agent.session?.active_break"
                        class="text-amber-11"
                      >
                        {{ t('WORKFORCE.STATUS.ON_BREAK') }}
                      </span>
                      <span v-else-if="agent.idle_event">
                        {{ Math.round(agent.idle_event.duration_seconds / 60) }}
                        {{ t('WORKFORCE.TABLE.MINUTES_SUFFIX') }}
                      </span>
                      <span v-else>—</span>
                    </td>
                    <td class="px-5 py-3">
                      {{ remainingShiftLabel(agent) }}
                    </td>
                    <td class="px-5 py-3">
                      <span v-if="agent.scheduled_now" class="text-emerald-11">
                        {{ t('WORKFORCE.LABELS.YES') }}
                      </span>
                      <span v-else class="text-n-slate-10">
                        {{ t('WORKFORCE.LABELS.NO') }}
                      </span>
                    </td>
                    <td class="px-5 py-3">
                      {{ timeSinceLabel(agent.time_since_last_reply_seconds) }}
                    </td>
                    <td class="px-5 py-3 aa-row-activity">
                      <span
                        class="inline-flex min-w-[92px] items-center justify-center rounded-full border px-2.5 py-1 text-xs font-semibold"
                        :class="
                          replyActivityToday(agent).tone === 'ok'
                            ? 'border-sky-9/30 bg-sky-3/40 text-sky-11'
                            : 'border-ruby-9/30 bg-ruby-3/40 text-ruby-11'
                        "
                        :title="replyActivityToday(agent).title"
                      >
                        {{ replyActivityToday(agent).label }}
                      </span>
                    </td>
                    <td class="px-5 py-3">
                      {{
                        timeSinceLabel(agent.time_since_last_activity_seconds)
                      }}
                    </td>
                    <td class="px-5 py-3 text-right">
                      <div class="flex justify-end gap-2">
                        <WorkforceButton
                          size="xs"
                          variant="ghost"
                          icon="i-lucide-shield-alert"
                          @click="openReassignment(agent)"
                        >
                          {{ t('WORKFORCE.LIVE.ACTIONS.REASSIGN') }}
                        </WorkforceButton>
                        <WorkforceButton
                          size="xs"
                          variant="ghost"
                          icon="i-lucide-refresh-cw"
                          @click="fetchLiveStatus"
                        >
                          {{ t('WORKFORCE.ACTIONS.REFRESH') }}
                        </WorkforceButton>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="expandedAgents.includes(agent.user_id)"
                    class="bg-n-solid-2/60"
                  >
                    <td />
                    <td colspan="11" class="px-5 pb-4">
                      <div class="grid gap-4 xl:grid-cols-2">
                        <div
                          class="rounded-xl border border-n-weak/60 bg-n-solid-1 px-4 py-3"
                        >
                          <h4
                            class="text-xs font-semibold uppercase tracking-wide text-n-slate-9"
                          >
                            {{ t('WORKFORCE.LIVE.CARDS.SESSION_TITLE') }}
                          </h4>
                          <ul class="mt-3 grid gap-2 text-xs text-n-slate-11">
                            <li class="flex items-center justify-between">
                              <span class="font-medium text-n-slate-12">{{
                                t('WORKFORCE.LIVE.CARDS.SHIFT_ELAPSED')
                              }}</span>
                              <span>{{ shiftElapsedLabel(agent) }}</span>
                            </li>
                            <li class="flex items-center justify-between">
                              <span class="font-medium text-n-slate-12">{{
                                t('WORKFORCE.LIVE.TABLE.REMAINING')
                              }}</span>
                              <span>{{ remainingShiftLabel(agent) }}</span>
                            </li>
                            <li class="flex items-center justify-between">
                              <span class="font-medium text-n-slate-12">{{
                                t('WORKFORCE.LIVE.CARDS.OCCUPANCY')
                              }}</span>
                              <span>{{ occupancyLabel(agent) }}</span>
                            </li>
                            <li class="flex items-center justify-between">
                              <span class="font-medium text-n-slate-12">{{
                                t('WORKFORCE.LIVE.CARDS.BREAK_TIMER')
                              }}</span>
                              <span>{{ breakTimerLabel(agent) }}</span>
                            </li>
                            <li class="flex items-center justify-between">
                              <span class="font-medium text-n-slate-12">{{
                                t('WORKFORCE.LIVE.METRIC.SCHEDULED_NOW')
                              }}</span>
                              <span>{{
                                agent.scheduled_now
                                  ? t('COMMON.YES')
                                  : t('COMMON.NO')
                              }}</span>
                            </li>
                          </ul>
                        </div>
                        <div
                          class="rounded-xl border border-n-weak/60 bg-n-solid-1 px-4 py-3"
                        >
                          <div class="flex items-center justify-between">
                            <h4
                              class="text-xs font-semibold uppercase tracking-wide text-n-slate-9"
                            >
                              {{ t('WORKFORCE.LIVE.CARDS.WORKLOAD_TITLE') }}
                            </h4>
                            <WorkforceButton
                              size="xs"
                              variant="ghost"
                              icon="i-lucide-shield-alert"
                              @click="openReassignment(agent)"
                            >
                              {{ t('WORKFORCE.LIVE.ACTIONS.REASSIGN') }}
                            </WorkforceButton>
                          </div>
                          <p
                            v-if="!agent.conversations?.length"
                            class="mt-3 text-xs text-n-slate-10"
                          >
                            {{ t('WORKFORCE.LIVE.WORKLOAD.EMPTY') }}
                          </p>
                          <ul
                            v-else
                            class="mt-3 grid gap-3 text-xs text-n-slate-12"
                          >
                            <li
                              v-for="conversation in agent.conversations"
                              :key="conversation.id"
                              class="rounded-lg border border-n-weak/40 bg-n-solid-2 px-3 py-2"
                            >
                              <div
                                class="flex items-start justify-between gap-3"
                              >
                                <div class="space-y-1">
                                  <p class="font-semibold text-n-slate-12">
                                    #{{ conversation.display_id }}
                                  </p>
                                  <p class="text-[11px] text-n-slate-9">
                                    {{
                                      waitingDurationLabel(
                                        conversation.waiting_seconds
                                      )
                                    }}
                                    ·
                                    {{
                                      conversation.priority ||
                                      t('WORKFORCE.LIVE.WORKLOAD.NO_PRIORITY')
                                    }}
                                  </p>
                                </div>
                                <div class="flex flex-col items-end gap-2">
                                  <span
                                    class="text-[10px] uppercase tracking-wide text-n-slate-9"
                                  >
                                    {{ conversation.status }}
                                  </span>
                                  <div class="flex flex-wrap justify-end gap-2">
                                    <WorkforceButton
                                      size="xs"
                                      variant="ghost"
                                      icon="i-lucide-bell-ring"
                                      @click="
                                        openReminderModal(agent, conversation)
                                      "
                                    >
                                      {{ t('WORKFORCE.LIVE.ACTIONS.REMIND') }}
                                    </WorkforceButton>
                                  </div>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <aside class="grid gap-4">
        <!-- HR Overview -->
        <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
          <div
            class="flex items-center justify-between border-b border-n-weak/60 px-5 py-3"
          >
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('WORKFORCE.LIVE.HR.TITLE') }}
              </h3>
              <p class="text-xs text-n-slate-10">
                {{ t('WORKFORCE.LIVE.HR.SUBTITLE') }}
              </p>
            </div>
          </div>
          <div class="grid gap-2 px-5 py-4 sm:grid-cols-2">
            <div
              v-for="card in hrOverviewCards"
              :key="card.key"
              class="rounded-lg border border-n-weak/60 bg-n-solid-3 px-3 py-2.5"
            >
              <p
                class="text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
              >
                {{ card.label }}
              </p>
              <p class="mt-1 text-lg font-bold text-n-slate-12">
                {{ card.value }}
              </p>
              <p v-if="card.hint" class="text-[11px] text-n-slate-9">
                {{ card.hint }}
              </p>
            </div>
          </div>
        </div>

        <!-- Alerts Feed -->
        <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
          <div
            class="flex items-center justify-between border-b border-n-weak/60 px-5 py-3"
          >
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('WORKFORCE.LIVE.ALERTS.TITLE') }}
              </h3>
              <p class="text-xs text-n-slate-10">
                {{ t('WORKFORCE.LIVE.ALERTS.SUBTITLE') }}
              </p>
            </div>
            <span
              v-if="alertFeed.length"
              class="rounded-full bg-ruby-3/40 px-2 py-0.5 text-[11px] font-semibold text-ruby-11"
            >
              {{ alertFeed.length }}
            </span>
          </div>
          <div class="px-5 py-4">
            <p v-if="!alertFeed.length" class="text-sm text-n-slate-10">
              {{ t('WORKFORCE.LIVE.ALERTS.EMPTY') }}
            </p>
            <ul v-else class="grid gap-2">
              <li
                v-for="(alert, index) in alertFeed"
                :key="`${alert.type}-${index}`"
                class="rounded-lg border border-n-weak/60 bg-n-solid-3 px-3 py-2 text-xs"
              >
                <div class="flex items-center gap-2 text-n-slate-12">
                  <span
                    class="inline-flex size-6 shrink-0 items-center justify-center rounded-full"
                    :class="[alertToneClass(alert)]"
                  >
                    <span class="size-3.5" :class="[alertIconClass(alert)]" />
                  </span>
                  <div class="min-w-0">
                    <p class="truncate font-semibold">
                      {{ alertAgentName(alert) }}
                    </p>
                    <p class="text-[11px] text-n-slate-10">
                      {{ alertMessage(alert) }}
                    </p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Activity History -->
        <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
          <div
            class="flex items-center justify-between border-b border-n-weak/60 px-5 py-3"
          >
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ t('WORKFORCE.LIVE.HISTORY.TITLE') }}
              </h3>
              <p class="text-xs text-n-slate-10">
                {{ t('WORKFORCE.LIVE.HISTORY.SUBTITLE') }}
              </p>
            </div>
          </div>
          <div class="px-5 py-4">
            <p v-if="!historySeries.length" class="text-sm text-n-slate-10">
              {{ t('WORKFORCE.LIVE.HISTORY.EMPTY') }}
            </p>
            <div v-else class="flex h-32 items-end gap-1">
              <div
                v-for="point in historySeries"
                :key="point.timestamp.getTime()"
                class="relative flex-1 rounded-md bg-n-solid-2"
              >
                <div
                  v-tooltip="{
                    content: t('WORKFORCE.LIVE.HISTORY.ACTIVE_TOOLTIP', {
                      count: point.activeAgents,
                      time: point.timestamp.toLocaleTimeString(),
                    }),
                  }"
                  class="absolute bottom-0 left-[20%] right-[20%] rounded-t bg-teal-9"
                  :style="{
                    height: `${Math.max(4, (point.activeAgents / historyMax.active) * 100)}%`,
                  }"
                />
                <div
                  v-tooltip="{
                    content: t('WORKFORCE.LIVE.HISTORY.BREAK_TOOLTIP', {
                      count: point.onBreak,
                      time: point.timestamp.toLocaleTimeString(),
                    }),
                  }"
                  class="absolute bottom-0 left-[5%] right-[5%] rounded-t bg-amber-9 opacity-70"
                  :style="{
                    height: `${Math.max(4, (point.onBreak / historyMax.break) * 100)}%`,
                  }"
                />
                <div
                  v-tooltip="{
                    content: t('WORKFORCE.LIVE.HISTORY.PENDING_TOOLTIP', {
                      count: point.pendingConversations,
                      time: point.timestamp.toLocaleTimeString(),
                    }),
                  }"
                  class="absolute bottom-0 left-[35%] right-[35%] rounded-t bg-ruby-9 opacity-80"
                  :style="{
                    height: `${Math.max(4, (point.pendingConversations / historyMax.pending) * 100)}%`,
                  }"
                />
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <WorkforceModal
      v-if="reassignState.open"
      v-model="reassignState.open"
      :title="
        t('WORKFORCE.LIVE.REASSIGN_MODAL.TITLE', {
          name: reassignState.agent?.name || '',
        })
      "
      :description="t('WORKFORCE.LIVE.REASSIGN_MODAL.DESCRIPTION')"
      :cancel-label="t('COMMON.CLOSE')"
      confirm-label=""
      :loading="reassignState.loading"
      width="3xl"
      @cancel="closeReassignment"
    >
      <div class="grid gap-3">
        <div v-if="reassignState.loading" class="py-6 text-sm text-n-slate-10">
          {{ t('WORKFORCE.LIVE.REASSIGN_MODAL.LOADING') }}
        </div>
        <div
          v-else-if="!reassignState.conversations.length"
          class="py-6 text-sm text-n-slate-10"
        >
          {{ t('WORKFORCE.LIVE.REASSIGN_MODAL.EMPTY') }}
        </div>
        <div
          v-for="conversation in reassignState.conversations"
          :key="conversation.id"
          class="grid gap-2 rounded-xl border border-n-weak/60 bg-n-solid-2 px-4 py-3 text-sm text-n-slate-12"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span class="font-semibold text-n-slate-12">
              #{{ conversation.display_id }}
            </span>
            <span class="text-xs uppercase tracking-wide text-n-slate-9">
              {{ conversation.status }}
            </span>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <label
              class="flex-1 text-xs uppercase tracking-wide text-n-slate-10"
            >
              {{ t('WORKFORCE.LIVE.REASSIGN_MODAL.SELECT_AGENT') }}
              <WorkforceSelect
                v-model="conversation.target_agent_id"
                class="mt-1"
              >
                <option :value="null">
                  {{ t('WORKFORCE.LIVE.REASSIGN_MODAL.UNASSIGN_OPTION') }}
                </option>
                <option
                  v-for="agentOption in targetOptionsFor(
                    reassignState.agent?.user_id
                  )"
                  :key="agentOption.id"
                  :value="agentOption.id"
                >
                  {{ agentOption.name }}
                </option>
              </WorkforceSelect>
            </label>
            <WorkforceButton
              size="sm"
              icon="i-lucide-send"
              @click="
                reassignConversation(
                  conversation.id,
                  conversation.target_agent_id
                )
              "
            >
              {{ t('WORKFORCE.LIVE.REASSIGN_MODAL.CONFIRM') }}
            </WorkforceButton>
          </div>
        </div>
      </div>
    </WorkforceModal>

    <WorkforceModal
      v-if="batchReassignState.open"
      v-model="batchReassignState.open"
      :title="
        t('WORKFORCE.LIVE.BATCH_REASSIGN.TITLE', {
          count: selectedAgentIds.length,
        })
      "
      :description="t('WORKFORCE.LIVE.BATCH_REASSIGN.DESCRIPTION')"
      :cancel-label="t('COMMON.CLOSE')"
      confirm-label=""
      :loading="batchReassignState.loading"
      width="4xl"
      @cancel="closeBatchReassignment"
    >
      <div class="grid gap-3">
        <div
          v-if="batchReassignState.loading"
          class="py-6 text-sm text-n-slate-10"
        >
          {{ t('WORKFORCE.LIVE.REASSIGN_MODAL.LOADING') }}
        </div>
        <div
          v-else-if="!batchReassignState.conversations.length"
          class="py-6 text-sm text-n-slate-10"
        >
          {{ t('WORKFORCE.LIVE.BATCH_REASSIGN.EMPTY') }}
        </div>
        <div
          v-for="conversation in batchReassignState.conversations"
          :key="conversation.id"
          class="grid gap-2 rounded-xl border border-n-weak/60 bg-n-solid-2 px-4 py-3 text-sm text-n-slate-12"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p class="font-semibold text-n-slate-12">
                #{{ conversation.display_id }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{
                  t('WORKFORCE.LIVE.BATCH_REASSIGN.OWNER', {
                    name: conversation.owner_name,
                  })
                }}
              </p>
            </div>
            <span class="text-xs uppercase tracking-wide text-n-slate-9">
              {{ conversation.status }}
            </span>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <label
              class="flex-1 text-xs uppercase tracking-wide text-n-slate-10"
            >
              {{ t('WORKFORCE.LIVE.REASSIGN_MODAL.SELECT_AGENT') }}
              <WorkforceSelect
                v-model="conversation.target_agent_id"
                class="mt-1"
              >
                <option :value="null">
                  {{ t('WORKFORCE.LIVE.REASSIGN_MODAL.UNASSIGN_OPTION') }}
                </option>
                <option
                  v-for="agentOption in targetOptionsFor(
                    conversation.owner_user_id
                  )"
                  :key="agentOption.id"
                  :value="agentOption.id"
                >
                  {{ agentOption.name }}
                </option>
              </WorkforceSelect>
            </label>
            <WorkforceButton
              size="sm"
              icon="i-lucide-send"
              @click="
                reassignConversation(
                  conversation.id,
                  conversation.target_agent_id,
                  { mode: 'batch' }
                )
              "
            >
              {{ t('WORKFORCE.LIVE.REASSIGN_MODAL.CONFIRM') }}
            </WorkforceButton>
          </div>
        </div>
      </div>
    </WorkforceModal>
    <WorkforceModal
      v-if="reminderState.open"
      v-model="reminderState.open"
      :title="
        t('WORKFORCE.LIVE.REMINDER_MODAL.TITLE', {
          name: reminderState.agent?.name || '',
        })
      "
      :description="t('WORKFORCE.LIVE.REMINDER_MODAL.DESCRIPTION')"
      :confirm-label="t('WORKFORCE.LIVE.REMINDER_MODAL.SEND')"
      :cancel-label="t('WORKFORCE.LIVE.REMINDER_MODAL.CANCEL')"
      :loading="reminderState.submitting"
      width="lg"
      @confirm="submitReminder"
      @cancel="closeReminderModal"
    >
      <div class="grid gap-4">
        <label
          v-if="reminderState.agent?.conversations?.length > 1"
          class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.LIVE.REMINDER_MODAL.SELECT_CONVERSATION') }}
          <WorkforceSelect v-model="reminderState.conversationId">
            <option
              v-for="conversation in reminderState.agent.conversations"
              :key="conversation.id"
              :value="conversation.id"
            >
              #{{ conversation.display_id }} ·
              {{ waitingDurationLabel(conversation.waiting_seconds) }}
            </option>
          </WorkforceSelect>
        </label>
        <div
          class="rounded-lg border border-n-weak/60 bg-n-solid-2 px-4 py-3 text-sm text-n-slate-12"
        >
          <p class="font-semibold">
            #{{ reminderState.conversation?.display_id || '—' }}
          </p>
          <p class="text-xs text-n-slate-9">
            {{
              t('WORKFORCE.LIVE.REMINDER_MODAL.WAITING', {
                value: waitingDurationLabel(
                  reminderState.conversation?.waiting_seconds
                ),
              })
            }}
          </p>
        </div>
        <label
          class="grid gap-1 text-xs uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.LIVE.REMINDER_MODAL.NOTES') }}
          <textarea
            v-model="reminderState.notes"
            rows="3"
            class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
            :placeholder="t('WORKFORCE.LIVE.REMINDER_MODAL.PLACEHOLDER')"
          />
        </label>
      </div>
    </WorkforceModal>
  </section>
</template>
